/**
 * The app's single Datadog entry point: Browser Logs + RUM (design §3.3,
 * §6.6; RUM per D3).
 *
 * Every function is safe to call before or without initialization and
 * never throws: observability must not break the page it observes. Logs
 * need `NEXT_PUBLIC_DD_CLIENT_TOKEN`; RUM also needs
 * `NEXT_PUBLIC_DD_RUM_APPLICATION_ID`. The client token is write-only, so
 * inlining it in the bundle is safe.
 *
 * Session Replay is deliberately disabled: recording the DOM would put the
 * pay page's PII into Datadog (same posture as query-string stripping and
 * the integer-only `usr.id`).
 */

import { datadogLogs } from "@datadog/browser-logs"
import { datadogRum } from "@datadog/browser-rum"

const DEFAULT_DD_SITE = "us3.datadoghq.com"
const DEFAULT_DD_SERVICE = "billdozer-frontend-browser"
const DEFAULT_RUM_SAMPLE_RATE = 100

// Tracked per SDK because RUM has a stricter gate: logs can be live while RUM is not.
let logsInitialized = false
let rumInitialized = false

export interface ApiErrorContext {
  method: string
  /** Request path; the query string is stripped before logging (§6.7). */
  path: string
  status: number
  detail?: unknown
}

/** Strip the query string from a URL/path — it can carry signed tokens (§6.7). */
function stripQuery(path: string): string {
  const queryIndex = path.indexOf("?")
  return queryIndex === -1 ? path : path.slice(0, queryIndex)
}

function describeError(err: unknown): {
  kind: string
  message: string
  stack?: string
} {
  if (err instanceof Error) {
    return { kind: err.name, message: err.message, stack: err.stack }
  }
  return { kind: "Error", message: String(err) }
}

function resolveRumSampleRate(): number {
  const raw = process.env.NEXT_PUBLIC_DD_RUM_SAMPLE_RATE
  if (raw === undefined || raw === "") return DEFAULT_RUM_SAMPLE_RATE
  const parsed = Number(raw)
  if (!Number.isFinite(parsed)) return DEFAULT_RUM_SAMPLE_RATE
  return Math.min(100, Math.max(0, parsed))
}

/**
 * RUM → backend APM correlation via trace headers on `/api` calls.
 * Same-origin only (the Next.js proxy), so no CORS preflight; empty
 * without a `window`, which harmlessly disables injection.
 */
function rumTracingUrls(): Array<{
  match: string
  propagatorTypes: Array<"tracecontext" | "datadog">
}> {
  if (typeof window === "undefined") return []
  return [
    { match: window.location.origin, propagatorTypes: ["tracecontext", "datadog"] },
  ]
}

/** Initialize the Logs + RUM SDKs once each from build-time `NEXT_PUBLIC_DD_*` env. */
export function initDatadog(): void {
  const clientToken = process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN
  if (!clientToken) return

  const site = process.env.NEXT_PUBLIC_DD_SITE || DEFAULT_DD_SITE
  const service = process.env.NEXT_PUBLIC_DD_SERVICE || DEFAULT_DD_SERVICE
  const env = process.env.NEXT_PUBLIC_DD_ENV
  const version = process.env.NEXT_PUBLIC_DD_VERSION

  if (!logsInitialized) {
    datadogLogs.init({
      clientToken,
      site,
      service,
      env,
      version,
      // Auto-capture uncaught exceptions + console errors as a safety net
      // beyond the explicit reportApiError/reportRenderError calls (§6.6).
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
    })
    logsInitialized = true
  }

  const applicationId = process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID
  if (applicationId && !rumInitialized) {
    datadogRum.init({
      applicationId,
      clientToken,
      site,
      service,
      env,
      version,
      sessionSampleRate: resolveRumSampleRate(),
      sessionReplaySampleRate: 0,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      // Inert while replay is off; masks inputs if replay is ever enabled.
      defaultPrivacyLevel: "mask-user-input",
      allowedTracingUrls: rumTracingUrls(),
    })
    rumInitialized = true
  }
}

/**
 * Log a failed API call as a structured `api_error` (design §4.3). Logs
 * only: RUM's resource tracking already captures the request.
 */
export function reportApiError(ctx: ApiErrorContext): void {
  if (!logsInitialized) return

  try {
    const message =
      ctx.detail === undefined
        ? "api_error"
        : `api_error ${JSON.stringify(ctx.detail)}`
    datadogLogs.logger.error(message, {
      http: {
        method: ctx.method,
        url_details: { path: stripQuery(ctx.path) },
        status_code: ctx.status,
      },
      error: {
        kind: "ApiError",
        message: `${ctx.status} ${stripQuery(ctx.path)}`,
      },
    })
  } catch {
    // Observability must never break the caller.
  }
}

/**
 * Log a render-boundary failure and forward it to RUM explicitly — the
 * React error boundary swallows it before `window.onerror` can.
 */
export function reportRenderError(err: unknown): void {
  if (logsInitialized) {
    try {
      datadogLogs.logger.error("render_error", { error: describeError(err) })
    } catch {
      // Observability must never break the caller.
    }
  }

  if (rumInitialized) {
    try {
      datadogRum.addError(err, { source: "render_boundary" })
    } catch {
      // Observability must never break the caller.
    }
  }
}

/** Stamp `usr.id` on logs + RUM. Integer PK only — never the email (PII, §6.7). */
export function setDatadogUser(id: number | string): void {
  if (logsInitialized) {
    try {
      datadogLogs.setUser({ id: String(id) })
    } catch {
      // Best-effort; never break auth resolution.
    }
  }

  if (rumInitialized) {
    try {
      datadogRum.setUser({ id: String(id) })
    } catch {
      // Best-effort; never break auth resolution.
    }
  }
}

/** Test seam: the shared-process unit suite would otherwise leak init flags across files. */
export function __resetDatadogForTests(): void {
  logsInitialized = false
  rumInitialized = false
}
